import React, { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, PlusIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import surgeonService from '../../services/surgeonService';
import { errText } from '../../services/scheduleService';

const inputCls = 'w-full rounded-lg border border-slate-300 p-2.5 text-sm outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40';

/**
 * SurgeonPicker — choose the surgeon from the practice's list, or add a new one
 * on the spot (name + practice number). Shows the practice number, or warns when
 * it is missing — it is needed on the claim.
 *   value: { surgeon_id, display_name, practice_number } | null
 *   fallbackName: a name typed before surgeons were a list ("Dr Pillay"), shown when nothing is linked
 *   onChange(surgeon | null)
 */
const SurgeonPicker = ({ value, fallbackName, onChange, disabled = false, compact = false }) => {
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const [adding, setAdding] = useState(null); // { title, surname, practice_number }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    if (!editing) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const rows = await surgeonService.list(q.trim());
        if (!cancelled) { setResults(rows.slice(0, 10)); setActive(0); }
      } catch (e) {
        if (!cancelled) setErr(errText(e));
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, editing]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setEditing(false); setAdding(null); } };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const pick = (s) => {
    onChange(s);
    setEditing(false);
    setQ('');
    setAdding(null);
    setErr('');
  };

  const startAdd = () => {
    const typed = q.trim().replace(/^(dr|prof|mr|mrs|ms)\.?\s+/i, '');
    setAdding({ title: 'Dr', surname: typed, practice_number: '' });
  };

  const create = async (confirmDuplicate = false) => {
    setBusy(true);
    setErr('');
    try {
      pick(await surgeonService.create({ ...adding, confirm_duplicate: confirmDuplicate }));
    } catch (e) {
      if (e.response?.data?.code === 'possible_duplicate') {
        setAdding((a) => ({ ...a, matches: e.response.data.matches }));
      } else {
        setErr(errText(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); setEditing(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (active < results.length) pick(results[active]);
      else if (q.trim()) startAdd();
    }
  };

  if (!editing) {
    return (
      <div className={`flex items-center gap-2 rounded-lg border border-slate-300 bg-white ${compact ? 'px-2 py-1.5' : 'px-2.5 py-2'}`}>
        <div className="min-w-0 flex-1">
          {value ? (
            <>
              <div className="truncate text-sm font-semibold text-slate-800">{value.display_name}</div>
              {value.practice_number ? (
                <div className="font-mono text-[11px] text-slate-500">Practice no. {value.practice_number}</div>
              ) : (
                <div className="flex items-center gap-1 text-[11px] font-semibold text-amber-700"><ExclamationTriangleIcon className="h-3.5 w-3.5" />No practice number</div>
              )}
            </>
          ) : fallbackName ? (
            <>
              <div className="truncate text-sm text-slate-700">{fallbackName}</div>
              <div className="text-[11px] text-amber-700">Not linked to a surgeon on the list</div>
            </>
          ) : (
            <div className="text-sm text-slate-400">No surgeon</div>
          )}
        </div>
        {!disabled && (
          <>
            <button type="button" onClick={() => { setEditing(true); setQ(value ? '' : (fallbackName || '').replace(/^(dr|prof)\.?\s+/i, '')); }} className="rounded-md px-2 py-1 text-xs font-semibold text-brand-teal hover:bg-teal-50">
              {value ? 'Change' : 'Choose'}
            </button>
            {value && (
              <button type="button" onClick={() => onChange(null)} title="Clear" className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600"><XMarkIcon className="h-4 w-4" /></button>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      {adding ? (
        <div className="space-y-2 rounded-lg border border-brand-teal/50 bg-teal-50/40 p-2.5">
          <div className="text-xs font-semibold text-slate-600">Add a surgeon</div>
          <div className="flex gap-2">
            <select value={adding.title} onChange={(e) => setAdding({ ...adding, title: e.target.value })} className={`${inputCls} w-20`}>
              {['Dr', 'Prof', 'Mr', 'Mrs', 'Ms'].map((t) => <option key={t}>{t}</option>)}
            </select>
            <input autoFocus value={adding.surname} onChange={(e) => setAdding({ ...adding, surname: e.target.value, matches: undefined })} placeholder="Surname" className={inputCls} />
          </div>
          <input value={adding.practice_number} onChange={(e) => setAdding({ ...adding, practice_number: e.target.value })} placeholder="BHF practice number (7 digits)" maxLength={9} className={`${inputCls} font-mono`} />
          {adding.matches && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              Already on the list:
              {adding.matches.map((m) => (
                <button key={m.surgeon_id} type="button" onClick={() => pick(m)} className="mt-1 flex w-full justify-between rounded bg-white px-2 py-1 text-left hover:bg-amber-100">
                  <span>{[m.title, m.initials, m.surname].filter(Boolean).join(' ')}{m.speciality ? ` · ${m.speciality}` : ''}</span>
                  <span className="font-semibold">Use</span>
                </button>
              ))}
            </div>
          )}
          {err && <p className="text-xs text-red-600">{err}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setAdding(null)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-white">Back</button>
            <button type="button" onClick={() => create(!!adding.matches)} disabled={busy || !adding.surname.trim()} className="rounded-md bg-brand-navy px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
              {busy ? 'Adding…' : adding.matches ? 'Add anyway' : 'Add surgeon'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Search surgeons — name, speciality or practice number" className={`${inputCls} pl-8`} />
          </div>
          <ul className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            {results.map((s, i) => (
              <li key={s.surgeon_id}>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)} onMouseEnter={() => setActive(i)} className={`flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm ${i === active ? 'bg-teal-50' : ''}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-slate-800">{[s.title, s.initials, s.surname].filter(Boolean).join(' ')}</span>
                    <span className="block text-[11px] text-slate-500">{[s.speciality, s.rooms_name].filter(Boolean).join(' · ') || '—'}</span>
                  </span>
                  {s.practice_number
                    ? <span className="font-mono text-[11px] text-slate-500">{s.practice_number}</span>
                    : <span className="rounded bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800">no number</span>}
                </button>
              </li>
            ))}
            <li>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={startAdd} onMouseEnter={() => setActive(results.length)} className={`flex w-full items-center gap-1.5 px-3 py-2 text-left text-sm font-semibold text-brand-navy ${active === results.length ? 'bg-teal-50' : ''}`}>
                <PlusIcon className="h-4 w-4" /> {q.trim() ? `Add “${q.trim()}” as a new surgeon` : 'Add a new surgeon'}
              </button>
            </li>
          </ul>
        </>
      )}
    </div>
  );
};

export default SurgeonPicker;
